import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Navigation from './components/Navigation';
import SEO from './components/SEO/SEO';
import { useDefaultSEO } from './hooks/useSEO';
import { WizardProvider, useWizard } from './components/wizard/WizardContext';
import { ThemeProvider } from './contexts/ThemeContext';
import WizardNavigation from './components/wizard/WizardNavigation';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';
import LoadStep from './components/steps/LoadStep';
import CropStep from './components/steps/CropStep';
import DownloadStep from './components/steps/DownloadStep';
import ProtectedRoute from './components/routing/ProtectedRoute';

// Legal & Trust Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';

const ClientAppContent: React.FC = () => {
  const { state } = useWizard();

  // SEO management
  const seoData = useDefaultSEO(state.selectedCountry);

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        image={seoData.image}
        country={seoData.country}
        language={seoData.language}
      />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navigation />

        <Routes>
          {/* Main Application Routes */}
          <Route
            path="/"
            element={
              <>
                <WizardNavigation />
                <div className="py-4">
                  <div className="mx-auto px-2 sm:px-4 w-full sm:w-[85%] md:w-[85%] lg:w-[85%] xl:w-[85%] 2xl:w-[85%]">
                    <LoadStep />
                  </div>
                </div>
              </>
            }
          />
          <Route
            path="/crop.html"
            element={
              <>
                <WizardNavigation />
                <div className="py-4">
                  <div className="mx-auto px-2 sm:px-4 w-full sm:w-[85%] md:w-[85%] lg:w-[85%] xl:w-[85%] 2xl:w-[85%]">
                    <ProtectedRoute requiredStep="load">
                      <CropStep />
                    </ProtectedRoute>
                  </div>
                </div>
              </>
            }
          />
          <Route
            path="/save.html"
            element={
              <>
                <WizardNavigation />
                <div className="py-4">
                  <div className="mx-auto px-2 sm:px-4 w-full sm:w-[85%] md:w-[85%] lg:w-[85%] xl:w-[85%] 2xl:w-[85%]">
                    <ProtectedRoute requiredStep="crop">
                      <DownloadStep />
                    </ProtectedRoute>
                  </div>
                </div>
              </>
            }
          />

          {/* Legal & Trust Pages */}
          <Route path="/privacy.html" element={<PrivacyPolicy />} />
          <Route path="/terms.html" element={<TermsOfService />} />
          <Route path="/cookies.html" element={<CookiePolicy />} />
          <Route path="/about.html" element={<About />} />
          <Route path="/contact.html" element={<Contact />} />
          <Route path="/faq.html" element={<FAQ />} />
        </Routes>

        <Footer />
        <CookieConsentBanner />
      </div>
    </>
  );
};

const ClientApp: React.FC = () => {
  return (
    <ThemeProvider>
      <WizardProvider>
        <ClientAppContent />
      </WizardProvider>
    </ThemeProvider>
  );
};

export default ClientApp;